// utils/parseHtmlWithAnchors.tsx
import parse, { DOMNode, Element } from 'html-react-parser';
import React, { ReactElement } from 'react';
import slugify from 'slugify';

export type Heading = {
  id: string;
  text: string;
  tag: string;
};

export function parseHtmlWithAnchors(html: string): {
  content: ReactElement;
  headings: Heading[];
} {
  const headings: Heading[] = [];

  const transform = (node: DOMNode, index: number): ReactElement | undefined => {
    if (node.type === 'tag' && ['h1', 'h2', 'h3'].includes(node.name)) {
      const elem = node as Element;

      const headingText = getTextContent(elem);
      const id = slugify(headingText, { lower: true, strict: true });

      headings.push({ id, text: headingText, tag: elem.name });

      // Clone the heading element and inject the anchor before it
      return (
        <React.Fragment key={index}>
          <span className='anchor' id={id} />
          {React.createElement(elem.name, { key: `${id}-heading` }, parse(domChildrenToHtml(elem.children)))}
        </React.Fragment>
      );
    }

    return undefined;
  };

  const content = <>{parse(html, { replace: transform })}</>;

  return { content, headings };
}

// Helper: Get plain text from nested children
function getTextContent(elem: Element): string {
  return elem.children
    .map(child => {
      if (child.type === 'text') return child.data;
      if (child.type === 'tag') return getTextContent(child as Element);
      return '';
    })
    .join('')
    .trim();
}

// Helper: Convert children back to raw HTML
function domChildrenToHtml(children: DOMNode[]): string {
  return children
    .map(child => {
      if (child.type === 'text') return child.data;
      if (child.type === 'tag') {
        const el = child as Element;
        const inner = domChildrenToHtml(el.children);
        return `<${el.name}>${inner}</${el.name}>`;
      }
      return '';
    })
    .join('');
}
